// src/components/ModalContainer.js

import React, { useContext, useMemo, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { ModalContext } from '../context/ModalContext';
import { TabContext } from '../context/TabContext';
import { useSwipeable } from 'react-swipeable';
import { RemoveScroll } from 'react-remove-scroll';
import { useLocation, useNavigate } from 'react-router-dom';  // ← Importamos
import './ModalContainer.css';

const ModalContainer = () => {
  const { modalStacks, closeModal } = useContext(ModalContext);
  const { activeTab } = useContext(TabContext);

  const navigate = useNavigate();                // ← Hook para navegar
  const location = useLocation();                // ← Hook para leer URL

    // Convertir modalStacks (objeto) en una lista de [tab, stack]
  const stacksEntries = useMemo(
    () => Object.entries(modalStacks),
    [modalStacks]
  );

  //console.log('[ModalContainer] activeTab:', activeTab, 'modalStacks:', modalStacks);

  // Estado para swipe en el modal superior de la pestaña activa
  const [translateX, setTranslateX] = useState(0);

  // Cada vez que cambie la pila, reseteamos translateX
  useEffect(() => {
    const activeStack = modalStacks[activeTab] || [];
    if (activeStack.length > 0) {
      setTranslateX(0);
    }
  }, [modalStacks, activeTab]);

  // Ajuste dinámico de altura de modales
  useEffect(() => {
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;
    const availableHeight = viewportHeight - 60 - 60; // 60px header + 60px footer
    document.documentElement.style.setProperty(
      '--modal-height',
      `${availableHeight}px`
    );
  }, []);

  // ───────────────────────────────────────────────────────────────────
  // FUNCION “cierre robusto” que quita ?modal=true solo si es el único modal
  const handleCloseTopModal = () => {
    const stack = modalStacks[activeTab] || [];
    // Si solo hay un modal en la pila (el que vino de la URL)
    // y la URL contiene '?modal=true', entonces limpiamos el query
    if (stack.length === 1 && location.search.includes('modal=true')) {
      navigate('/', { replace: true });
    }
    // Finalmente cerramos el modal superior (sea único o no)
    closeModal(activeTab);
  };
  // ───────────────────────────────────────────────────────────────────

  // Swipe handlers: si swipe correcto, llamamos a handleCloseTopModal()
  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      const origin = eventData.event.target;
      if (origin.closest('.embla-carousel__wrapper')) return;
      if (eventData.dir === 'Right') {
        setTranslateX(eventData.deltaX);
      }
    },
    onSwiped: (eventData) => {
      const origin = eventData.event.target;
      if (origin.closest('.embla-carousel__wrapper')) {
        setTranslateX(0);
        return;
      }
      if (eventData.dir === 'Right' && eventData.deltaX > 200) {
        handleCloseTopModal();
      } else {
        setTranslateX(0);
      }
    },
    delta: 10,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
    trackMouse: false,
  });

    // Ajuste de altura de los modales (Viewport - Header - Footer)
  useEffect(() => {
    const viewportHeight = window.visualViewport
      ? window.visualViewport.height
      : window.innerHeight;
    const availableHeight = viewportHeight - 60 - 60; // 60px header + 60px footer
    document.documentElement.style.setProperty(
      '--modal-height',
      `${availableHeight}px`
    );
  }, []);


  return ReactDOM.createPortal(
    <>
      {/*
        Renderizamos todas las pilas de modales (una por pestaña),
        pero ocultamos (display: none) las que no correspondan a activeTab.
      */}
      {stacksEntries.map(([tab, stack]) =>
        stack.map((modal, index) => {
          const key = modal.id; // ID único generado en ModalContext
          // Solo la pila activa participa del z-index y del swipe
          const isActiveTab = tab === activeTab;
          const isTopModal = isActiveTab && index === stack.length - 1;
          const zIndex = 1000 + (isActiveTab ? index : 0);

          //console.log(`[ModalContainer] rendering modal id=${key} tab=${tab} idx=${index} activeTab=${activeTab}`);

          return (
            <div
              key={key}
              className={`modal-overlay ${modal.overlayClassName || ''}`}
              style={{
                display: isActiveTab ? 'flex' : 'none',
                zIndex,
              }}
              // Si es el modal superior, clic fuera lo cierra.
              onClick={isTopModal ? handleCloseTopModal : undefined}
              onTouchMove={(e) => e.preventDefault()}
            >
              {isActiveTab ? (
                // Para el modal activo, bloqueamos el scroll del body
                <RemoveScroll enabled={true}>
                  <div
                    className="modal-content"
                    // Sólo el modal superior maneja swipe
                    {...(isTopModal && !modal.disableSwipe ? swipeHandlers : {})}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      transform: isTopModal
                        ? `translateX(${translateX}px)`
                        : 'translateX(0)',
                    }}
                  >
                    {modal.content}
                  </div>
                </RemoveScroll>
              ) : (
                // Para modales de otras pestañas, no bloqueamos scroll
                <div
                  className="modal-content"
                  {...(isTopModal && !modal.disableSwipe ? swipeHandlers : {})}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    transform: isTopModal
                      ? `translateX(${translateX}px)`
                      : 'translateX(0)',
                  }}
                >
                  {modal.content}
                </div>
              )}
            </div>
          );
        })
      )}
    </>,
    document.getElementById('modal-root')
  );
};

export default ModalContainer;
